import React, { useEffect, useState } from "react";
import dayjs from 'dayjs'

import PropTypes from "prop-types";

// apollo & operations
import { QUERY_DISTRICT_REPORTS } from "../../../../operations/queries/districtreports";

// ant design
import { Form, Button, Card, Col, Row, Table, Typography } from "antd";
const { Paragraph, Text } = Typography

import ReportDistributionModal from "./ReportDistributionModal"

import ReactDragListView from 'react-drag-listview'
import './ReportBuilder.css'

//Components
import { exportToPDF } from "./ExportToPDF";
import { exportToCSV } from "./ExportToCSV";

//Functions
import { 
  formatPhoneNumber,
  getReportTableColumnFilterValues, 
  normalizeReportTableData 
} from "./Functions";

import {
  DeleteOutlined,
  ExportOutlined,
} from '@ant-design/icons'

const ReportTable = ({
  form,
  reportGroup,
  queryReportData,

  reportDefsRef,
  reportDefRef,
  selectedReportDefId,
  setSelectedReportDefId,

  reportPreviewTitle,
  reportPreviewNotes,
  reportTableColumns,
  setReportTableColumns,
  reportTableExtraData,
  setreportTableExtraData,
  reportTableExtraDatasource,
  setreportTableExtraDatasource,
  reportTableHasFilters,
  setReportTableHasFilters,
  reportTableHasSorts,
  setReportTableHasSorts,
  reportTableSortState,
  setReportTableSortState,

  reportDefChanged,
  setReportDefChanged,

  reportTableName,
  reportTableLabel,
  reportTableStyle,
  reportTableScroll,
  reportTableVisible,

  isMountedRef,
  
  // validateStatus,
  // help,
  // hasFeedback,
}) => {
  const [isReportDistributionModalOpen, setIsReportDistributionModalOpen] = useState(false);
  const showReportDistributionModal = () => {setIsReportDistributionModalOpen(true)}
  const [isGettingData, setIsGettingData] = useState(false);
  const [isExportingCsv, setIsExportingCsv] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  useEffect(() => {
    if (reportTableSortState.field && reportTableSortState.order) {
      // No need to fire onChange
      // Just make sure your sortedData uses sortState
    }
  }, [reportTableSortState]);

  //--------
  //Handle any changes to table.
  //--------
  const handleReportTableOnChange = (newPagination, filters, sorter, extra) => {
    //Save report's currentDataSource(e.g. reportTableExtraData filtered and sorted) to global for reference by csv export and pdf generation
    if (extra) {
      setreportTableExtraData(extra?.currentDataSource)
    } else {
      setreportTableExtraData(currentDataSource)
    }

    //Handle saved sorter
    if (sorter.field && sorter.order) {
      setReportTableSortState({
        field: sorter.field,
        order: sorter.order,
      });
    }

    //Handle table sort
    if (extra?.action === 'sort') {
      sortTable(sorter.field, sorter.order)

      const tableHasSorts = Array.isArray(sorter) ? 
        sorter.some((sort) => sort.order) :
        !!sorter.order; //Are there any sorts?
      setReportTableHasSorts(tableHasSorts)

      let updatedDefRef = {...reportDefRef.current}
      updatedDefRef.reportTableSorter = sorter
      reportDefRef.current = updatedDefRef
    }

    //Handle table filter
    if (extra?.action === 'filter') {
      filterTable(filters)

      const tableHasFilters = Object.values(filters).some(  //Are there any filters set?
        (value) => Array.isArray(value) && value.length > 0
      );
      setReportTableHasFilters(tableHasFilters)
      
      let updatedDefRef = {...reportDefRef.current}
      updatedDefRef.reportTableFilters = filters
      reportDefRef.current = updatedDefRef

    }

    //Handle table pagination(page change or rows/page change)
    if (extra?.action === 'paginate') {
      setPagination({
        current: newPagination.current,
        pageSize: newPagination.pageSize,
      });
    }
  }
  
  const handleClearSort = () => {
    sortTable('', null)

    //Clear serter in reportDefRef
    let updatedDefRef = {...reportDefRef.current}
    updatedDefRef.reportTableSorter = {}
    reportDefRef.current = updatedDefRef

    //Update state
    setReportTableHasSorts(false)
  }

  const handleClearFilters = () => {
    let newColumns = reportTableColumns

    //Clear existing filters in reportTableColumns object
    for (const newColumn of newColumns) {
      newColumn.filtered = false
      newColumn.filteredValue = null
    }

    //Clear filters in reportDefRef
    let updatedDefRef = {...reportDefRef.current}
    updatedDefRef.reportTableFilters = {}
    reportDefRef.current = updatedDefRef

    //Update state
    setReportTableColumns([...newColumns])
    setReportDefChanged(true)
    setReportTableHasFilters(false)
  }
  
  //--------
  //Sort table.
  //Call with empty fieldName and null order to clear existing sort.
  //--------
  const sortTable = (fieldName, order) => {
    //--------
    //Get column index of column containing fieldName.
    //--------
    const columnIndex = getFieldNameColumnIndex(fieldName)
    if (fieldName !== '' && columnIndex < 0) {
      return
    } //Bad fieldName

    //--------
    //Clear any existing sortOrders in reportTableColumns object.
    //--------
    let newColumns = reportTableColumns
    for (const column of reportTableColumns) {
      column.sortOrder = null
    }

    //--------
    //Set the column's sortOrder if this isn't a 'clear sort' request.
    //--------
    if (columnIndex >= 0) {
      newColumns[columnIndex].sortOrder = order
    }

    //--------
    //Set reportTableColumns state.  Table sorted on next render.
    //--------
    setReportTableColumns([...newColumns])
    setReportDefChanged(true)
    setReportTableHasSorts(true)
  }

  //--------
  //Filter table.
  //Call with empty fieldName and null order to clear existing sort.
  //--------
  const filterTable = filters => {
    let newColumns = reportTableColumns

    //filters is an object with a prop for each fieldName and a value containing any selected filter values
    //(e.g. schoolName: ['Test School 1', 'Test School 2']... )
    const filterProps = Object.keys(filters) //Get an array of properties from filters object

    //Clear existing filters in reportTableColumns object
    for (const filterProp of filterProps) {
      const fieldName = filterProp
      const columnIndex = getFieldNameColumnIndex(fieldName)
      if (columnIndex >= 0) {
        newColumns[columnIndex].filtered = false
        newColumns[columnIndex].filteredValue = null
      }
    }

    //Set filters where prop value is non-null.  Skip if a "clearFilters" request was made.
    for (const filterProp of filterProps) {
      const fieldName = filterProp
      const filterValues = filters[filterProp]
      const columnIndex = getFieldNameColumnIndex(fieldName)
      if (filterValues && columnIndex >= 0) {
        newColumns[columnIndex].filtered = true
        newColumns[columnIndex].filteredValue = filterValues
      }
    }

    //Update state
    setReportTableColumns([...newColumns])
    setReportDefChanged(true)
  }

  //--------
  //Get data.  PgetReportData passed from individual report code
  //--------
  const getReportData = async () => {
    setIsGettingData(true);

    //Query the database
    let newTableData = []
    const queryResults = await queryReportData()

    newTableData = await normalizeReportTableData(queryResults,reportDefRef.current)
    console.log('after normalize', newTableData,pagination,reportTableColumns)
  
    //Refresh the column filter values based on new data
    if (newTableData) {
      const newReportColumns = reportTableColumns
      const selectedFieldNames = reportDefRef.current.selectedFields.map((field) => field.fieldName)
      for (const field of selectedFieldNames) {
        const filterValues = getReportTableColumnFilterValues(
          field,
          newTableData,
          reportDefRef,
        )
        // filterValues.sort((a, b) => a - b);
        const columnIndex = getFieldNameColumnIndex(field)
        if (columnIndex >= 0 && filterValues?.length > 0) {
          newReportColumns[columnIndex].filters = filterValues
        } else if (columnIndex >= 0) {
          newReportColumns[columnIndex].filters = []
        } 
      }
      // setReportTableColumns([...newReportColumns])
    }

    setIsGettingData(false);
  }

  //--------
  //Report table column drag end
  //--------
  const onDragEnd = (fromIndex, toIndex) => {
    //Reorder report table columns
    let newColumns = []
    setReportTableColumns(prev => {
      newColumns = [...prev]
      const item = newColumns.splice(fromIndex, 1)[0]
      newColumns.splice(toIndex, 0, item)
      return newColumns
    })

    //Reorder selectedFields in reportDefRef
    const existingSelectedFields = reportDefRef.current.selectedFields
    const newSelectedFields = newColumns
    .map(column => existingSelectedFields.find(field => field.fieldName === column.dataIndex))
    .filter(filter => filter !== undefined); // <- In case something is missing

    const updatedDefRef = {...reportDefRef.current}
    updatedDefRef.selectedFields = newSelectedFields
    reportDefRef.current = {...updatedDefRef}

    //Update selections in available fields selector
    const selectedFieldNames = newSelectedFields.map((field) => field.fieldName);
    form.setFieldValue('availableFieldsTreeSelect', selectedFieldNames);

    setReportDefChanged(!reportDefChanged) //just here because I need a state change to make save button pulse
  }

  //--------
  //Returns fieldName's reportTable column index
  //--------
  const getFieldNameColumnIndex = fieldName => {
    let columnIndex = -1
    //const column = reportTableColumns.filter((column) => { return column.dataIndex === fieldName })
    for (let i = 0; i < reportTableColumns.length; i++) {
      if (reportTableColumns[i].dataIndex === fieldName) {
        columnIndex = i
        break
      }
    }
    return columnIndex
  }

  const handleExportToPdf = async () => {
    setIsExportingPdf(true)
    exportToPDF(reportDefRef, reportTableColumns, reportTableExtraData, "download")
        //await new Promise((resolve) => setTimeout(resolve, 2000));  //short wait for testing
    setIsExportingPdf(false)
  }
  const handleExportToCsv = async () => {
    setIsExportingCsv(true)
    exportToCSV(reportDefRef.current, reportTableColumns, reportTableExtraData, 'download')
    //await new Promise((resolve) => setTimeout(resolve, 2000));  //short wait for testing
    setIsExportingCsv(false)
  }

  return (
    <>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '8px',
          marginTop: '12px',
          marginBottom: '24px',
        }}
      >
        {/* Group 1: First 4 buttons */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <Form.Item>
            <Button
              disabled={!selectedReportDefId}
              onClick={getReportData}
              loading={isGettingData}
            >
              Get Report Data
            </Button>
          </Form.Item>
          <Form.Item>
            <Button
              key="exportToPdf"
              disabled={!selectedReportDefId}
              loading={isExportingPdf}
              onClick={handleExportToPdf}
            >
              Export to PDF
            </Button>
          </Form.Item>
          <Form.Item>
            <Button
              key="exportToCsv"
              disabled={!selectedReportDefId}
              loading={isExportingCsv}
              onClick={handleExportToCsv}
            >
              Export to CSV
            </Button>
          </Form.Item>
          <Form.Item>
            <Button
              disabled={!selectedReportDefId}
              onClick={showReportDistributionModal}
              icon={<ExportOutlined />}
            >
              Distribute Report
            </Button>
          </Form.Item>
        </div>

        {/* Spacer between groups */}
        <div style={{ width: '32px' }} />

        {/* Group 2: Clear buttons */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <Form.Item>
            <Button
              disabled={!reportTableHasFilters}
              onClick={handleClearFilters}
              style={
                reportTableHasFilters
                  ? { color: '#135200', fontWeight: 'bold' }
                  : {}
              }
            >
              {reportTableHasFilters ? 'Clear Filters' : 'No Filters'}
            </Button>
          </Form.Item>
          <Form.Item>
            <Button
              disabled={!reportTableHasSorts}
              onClick={handleClearSort}
              style={
                reportTableHasSorts
                  ? { color: '#135200', fontWeight: 'bold' }
                  : {}
              }
            >
              {reportTableHasSorts ? 'Clear Sorts' : 'No Sorts'}
            </Button>
          </Form.Item>
        </div>
      </div>

      <Card style={{ border: '1px solid #ccc', boxShadow: 'none', padding: '4px', marginBottom: '20px' }}>

        {selectedReportDefId &&
          <>
            <Text style={{ marginTop: "0px", fontSize: "10px", display: "block",  textAlign: 'center', fontStyle: "normal", lineHeight: 1.05 }}>
                {reportDefRef?.current?.district?.name}
            </Text>
            <Text style={{ fontSize: "10px", display: "block",  textAlign: 'center', fontStyle: "normal", lineHeight: 1.05 }}>
                {reportDefRef?.current?.district?.address}
            </Text>
            <Text style={{ marginTop: "0px", fontSize: "10px", display: "block",  textAlign: 'center', fontStyle: "normal", lineHeight: 1.05 }}>
              {`${reportDefRef?.current?.district?.city}, ${reportDefRef?.current?.district?.state}   ${reportDefRef?.current?.district?.zip}`}
            </Text>
            <Text style={{ marginTop: "0px", fontSize: "10px", display: "block",  textAlign: 'center', fontStyle: "normal", lineHeight: 1.05 }}>
              {`Phone: ${formatPhoneNumber(reportDefRef?.current?.district?.phone)}    Fax: ${formatPhoneNumber(reportDefRef?.current?.district?.fax)}`}
            </Text>
            <Text style={{ marginBottom: "16px", fontSize: "10px", display: "block",  textAlign: 'center', fontStyle: "normal", lineHeight: 1.05 }}>
              {`Website: ${reportDefRef?.current?.district?.website}`}
            </Text>
        
            <Paragraph style={{ marginTop: "16px", marginBottom: "8px", fontSize: "14px", fontWeight: "bold", textAlign: "center", margin: 0, padding: "2px", backgroundColor: "#f0f0f0", borderTop: '2px solid #000', borderBottom: '2px solid #000' }}>
              {reportPreviewTitle || ''}
            </Paragraph>

            {reportPreviewNotes &&
              <>
                <Text style={{ marginTop: "8px", display: "block",  textAlign: "left", border: "1px solid #d9d9d9", padding: "4px" }}>
                  {reportPreviewNotes}
                </Text>
              </>
            }
          </>
        }

        <ReactDragListView.DragColumn
          onDragEnd={onDragEnd}
          nodeSelector='th'
          handleSelector='.dragHandler'
          ignoreSelector='react-resizable-handle'
        >
          <Table
            name={reportTableName}
            label={reportTableLabel}
            style={reportTableStyle}
            scroll={reportTableScroll}
            visible={reportTableVisible}
            columns={reportTableColumns}
            dataSource={reportTableExtraDatasource}
            rowKey={(_, index) => index}

            onChange={handleReportTableOnChange}
            // pagination={true}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              pageSizeOptions: ['5','10', '20', '50'],
              position: ['topRight'],
              showTotal: (total, range) => `${range[0]}–${range[1]} of ${total} items`,
            }}

          />
        </ReactDragListView.DragColumn>

        {/* <ReportTable label='Results Table' name='reportTable' /> */}
        {selectedReportDefId &&
          <>
            <Row justify="space-between" align="middle" style={{ marginTop: "8px",  fontSize: "8px" }}>
              <Col flex="none">
                <Text style={{ textAlign: "left" }}>
                  {`${reportDefRef?.current?.reportName}`}
                </Text>
              </Col>
              <Col  flex="auto" style={{ display: "flex", justifyContent: "center" }}>
                <Text style={{ textAlign: "center" }}>
                  Page 1 of 1
                </Text>
              </Col>
              <Col flex="none">
                <Text style={{ textAlign: "right" }}>
                  {`${new Date().toLocaleString()}`}
                </Text>
              </Col>
            </Row>
          </>
        }
      </Card>

      {isReportDistributionModalOpen && (
        <ReportDistributionModal
        isReportDistributionModalOpen={isReportDistributionModalOpen}
        setIsReportDistributionModalOpen={setIsReportDistributionModalOpen}
        reportDefRef={reportDefRef}
        reportTableColumns={reportTableColumns}
        reportTableExtraData={reportTableExtraData}
        showSendButton={true}
        width={500}
        />
      )}

    </>
  )

};

// ReportTable.propTypes = {
//   label: PropTypes.string,
//   name: PropTypes.string,
//   help: PropTypes.help,
//   validateStatus: PropTypes.string,
//   hasFeedback: PropTypes.bool,
//   mode: PropTypes.string,
//   districtIds: PropTypes.string,
// };

export default ReportTable;
